import React from 'react';
import {View} from 'react-native';
import {toInteger} from 'lodash';

import Avatar from '/component/base/Avatar';
import ProgressBar from '/component/base/ProgressBar';
import Text from '/component/base/Text';
import {CoachInfoCard, Rank} from '/constant/TestIDs';
import {withTranslation} from '/provider/TranslationProvider';
import {generateComponentCallback} from '/util/component.util';
import {withHOC} from '/util/hoc.util';
import {getFontFamily} from '/util/text.util';

import {styles} from './CoachInfo.style';

// Define the types for the component props
interface CoachInfoProps {
  tt: (key: string, component: React.ReactNode) => React.ReactNode;
  reportRecordData: {
    firstName: string;
    lastName: string;
    avatarUrl: string;
    accountEmail: string;
    percentProgressToNextRank: string;
    rank: {
      description: string;
    };
  };
  hasNextRank: boolean;
}

export const CoachInfo: React.FC<CoachInfoProps> = ({
  tt = () => null, // default function for tt if it's not provided
  reportRecordData = {
    firstName: '',
    lastName: '',
    avatarUrl: '',
    accountEmail: '',
    percentProgressToNextRank: '0',
    rank: {description: ''},
  }, // default empty object for reportRecordData if it's not provided
  hasNextRank = false, // default false for hasNextRank if it's not provided
}) => {
  const {
    firstName,
    lastName,
    avatarUrl,
    accountEmail,
    percentProgressToNextRank,
    rank: {description: rankDescription} = {},
  } = reportRecordData;

  return (
    <View style={styles.header} accessible={false}>
      <Avatar
        size={56}
        isCoach={true}
        avatarUrl={avatarUrl}
        avatarKey={accountEmail}
      />
      <View style={styles.headerText}>
        <View accessible={true} accessibilityLabel={`${firstName} ${lastName}`}>
          <Text testID={CoachInfoCard.coachName} variant="button">
            {firstName} {lastName}
          </Text>
        </View>
        <View accessible={true} accessibilityLabel={rankDescription}>
          <Text testID={CoachInfoCard.coachRank} emphasis="medium">
            {rankDescription}
          </Text>
        </View>
        {hasNextRank ? (
          <View
            style={styles.progress}
            accessible={true}
            accessibilityLabel={`Progress to next rank: ${toInteger(
              percentProgressToNextRank,
            )} percent`}>
            <View
              style={styles.progressBarContainer}
              testID={Rank.rankProgressBar}>
              <ProgressBar
                style={{fill: styles.progressBarFill}}
                progress={toInteger(percentProgressToNextRank)}
              />
            </View>
            {tt(
              'rankUpProgress',
              generateComponentCallback({
                component: (
                  <Text
                    testID={CoachInfoCard.nextRankValue}
                    style={{flexShrink: 1}}
                    numberOfLines={1}
                    ellipsizeMode="tail">
                    <Text
                      testID={CoachInfoCard.nextRankValue}
                      emphasis="medium"
                      variant="caption"
                      style={getFontFamily('bold')}>
                      {toInteger(percentProgressToNextRank)}%{' '}
                    </Text>
                    <Text
                      emphasis="medium"
                      variant="caption"
                      testID={CoachInfoCard.nextRankText}>
                      to next rank
                    </Text>
                  </Text>
                ),
              })(),
            )}
          </View>
        ) : null}
      </View>
    </View>
  );
};

// Wrap the component with HOC for translation and other props
export default withHOC(withTranslation('reportCard.coach'))(CoachInfo);
